import React, { useState } from "react";

import { MCPConnectionPanel } from "mcp/components/MCPConnectionPanel";
import { MCPResourcesPanel } from "mcp/components/MCPResourcesPanel";
import { MCPToolsPanel } from "mcp/components/MCPToolsPanel";
import { CindraChat } from "cindra/CindraChat";
import { useMCP } from "mcp/hooks/useMCP";

export const MCPDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState<"chat" | "tools" | "resources" | "connection">("chat");
    const mcpState = useMCP();

    // If Cindra is disabled, show disabled state
    if (!mcpState.isCindraEnabled) {
        return (
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Karuna AI Dashboard</h1>
                            <p className="mt-2 text-lg text-gray-600">
                                AI-powered assistant with access to your healthcare data through MCP servers.
                            </p>
                        </div>
                        {/* Disabled Status */}
                        <div className="flex flex-col items-end space-y-1">
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                                <span className="text-sm text-gray-600">Cindra AI Disabled</span>
                            </div>
                            <div className="text-xs text-gray-500">Contact administrator to enable</div>
                        </div>
                    </div>
                </div>
                {/* Disabled State Content */}
                <div className="bg-gray-50 border border-gray-300 rounded-lg shadow-sm p-12">
                    <div className="text-center space-y-6">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="h-6 w-6 bg-gray-400 rounded-full"></div>
                            <h2 className="text-2xl font-bold text-gray-700">Cindra AI Features Disabled</h2>
                        </div>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Cindra AI features are currently disabled in this environment.
                            These features provide powerful AI-driven healthcare insights and automation capabilities.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">🤖 AI Assistant</h3>
                                <p className="text-gray-600 text-sm">
                                    Chat with your Karuna AI assistant for clinical decision support,
                                    data analysis, and automated reporting.
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">🔧 MCP Tools</h3>
                                <p className="text-gray-600 text-sm">
                                    Access healthcare data through Model Context Protocol (MCP) servers
                                    for real-time clinical information.
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">📁 Data Resources</h3>
                                <p className="text-gray-600 text-sm">
                                    Browse and analyze healthcare resources including patient data,
                                    clinical guidelines, and research materials.
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">🔗 Server Connections</h3>
                                <p className="text-gray-600 text-sm">
                                    Connect to external healthcare systems and clinical databases
                                    through secure MCP server connections.
                                </p>
                            </div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                            <h4 className="text-md font-semibold text-blue-800 mb-2">Enable Cindra AI Features</h4>
                            <p className="text-sm text-blue-700 mb-3">
                                To enable these features, contact your system administrator to set the
                                VITE_CINDRA_ENABLED environment variable to true.
                            </p>
                            <div className="bg-blue-100 border border-blue-300 rounded p-3">
                                <code className="text-xs text-blue-800 font-mono">VITE_CINDRA_ENABLED=true</code>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const tabs = [
        {
            id: "chat" as const,
            label: "Chat",
            icon: "🤖",
            description: "Chat with the Cindra AI assistant"
        },
        {
            id: "tools" as const,
            label: `Tools (${mcpState.tools.length})`,
            icon: "🔧",
            description: "Available MCP tools and functions"
        },
        {
            id: "resources" as const,
            label: `Resources (${mcpState.resources.length})`,
            icon: "📁",
            description: "MCP resources and data sources"
        },
        {
            id: "connection" as const,
            label: "Connection",
            icon: "🔗",
            description: "MCP server connection and authentication settings"
        }
    ];

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-0" aria-label="Tabs">
                        {
                            tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={
                                        `flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors min-w-0 flex-1 justify-center ${
                                            activeTab === tab.id
                                                ? "border-blue-500 text-blue-600 bg-blue-50"
                                                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                        }`
                                    }
                                >
                                    <span className="text-lg">{tab.icon}</span>
                                    <span className="truncate">{tab.label}</span>
                                </button>
                            ))
                        }
                    </nav>
                </div>
                {/* Tab Content - consistent height for all tabs */}
                <div className="overflow-y-auto" style={{ height: "calc(100vh - 240px)" }}>
                    {
                        activeTab === "chat" && (
                            <div className="h-full">
                                <CindraChat />
                            </div>
                        )
                    }
                    {
                        activeTab === "tools" && (
                            <div className="p-6">
                                <MCPToolsPanel />
                            </div>
                        )
                    }
                    {
                        activeTab === "resources" && (
                            <div className="p-6">
                                <MCPResourcesPanel />
                            </div>
                        )
                    }
                    {
                        activeTab === "connection" && (
                            <div className="p-6">
                                <MCPConnectionPanel />
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};
